import { Formik, Form } from "formik";
import TextField from "../components/common/TextField";
import Button from "../components/common/Button";
import RadioGroup from "../components/common/RadioGroup";
import {
  FaTag,
  FaEuroSign,
  FaSortNumericDown,
  FaBoxOpen,
} from "react-icons/fa";

interface ModifierOptionFormProps {
  initialValues: any;
  validationSchema: any;
  onSubmit: (
    values: any,
    formikHelpers: {
      resetForm: () => void;
    }
  ) => Promise<void>;
  isLoading?: boolean;
}

const vatOptions = [
  { label: "0%", value: 0 },
  { label: "10%", value: 10 },
  { label: "20%", value: 20 },
];

const ModifierOptionForm: React.FC<ModifierOptionFormProps> = ({
  initialValues,
  validationSchema,
  onSubmit,
  isLoading,
}) => {
  return (
    <Formik
      initialValues={{
        ...initialValues,
        vatRatePercentage: initialValues.vatRatePercentage || 10,
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting,
        setFieldValue,
      }) => {
        return (
          <Form className="max-w-4xl mx-auto w-full pb-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column - General Info */}
              <div className="space-y-6">
                {/* Basic Info Card */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-2">
                    <FaTag className="text-primary" />
                    <h3 className="font-bold text-gray-800 text-lg">
                      Informations Générales
                    </h3>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="displayName"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Nom de l&apos;option
                      </label>
                      <TextField
                        id="displayName"
                        value={values.displayName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        placeholder="Ex: Fromage supplémentaire"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="displayOrder"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Ordre d&apos;affichage
                      </label>
                      <TextField
                        id="displayOrder"
                        value={values.displayOrder}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        placeholder="Ex: 1"
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                {/* Pricing Card */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-2">
                    <FaEuroSign className="text-primary" />
                    <h3 className="font-bold text-gray-800 text-lg">
                      Prix & TVA
                    </h3>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="price"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Prix (€)
                      </label>
                      <TextField
                        id="price"
                        value={values.price}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        placeholder="0.00"
                        className="w-full font-mono"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Taux de TVA
                      </label>
                      <RadioGroup
                        label=""
                        options={vatOptions}
                        value={values.vatRatePercentage}
                        onChange={(val) =>
                          setFieldValue("vatRatePercentage", Number(val))
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Quantity Rules */}
              <div className="space-y-6">
                {/* Quantity Info Card */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 h-full">
                  <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-2">
                    <FaSortNumericDown className="text-primary" />
                    <h3 className="font-bold text-gray-800 text-lg">
                      Règles de Quantité
                    </h3>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label
                        htmlFor="quantityInfo.minPermitted"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Min. autorisé
                      </label>
                      <TextField
                        id="quantityInfo.minPermitted"
                        value={values.quantityInfo.minPermitted}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        placeholder="0"
                        className="w-full"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Quantité minimale que le client peut sélectionner pour
                        cette option.
                      </p>
                    </div>

                    <div>
                      <label
                        htmlFor="quantityInfo.maxPermitted"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Max. autorisé
                      </label>
                      <TextField
                        id="quantityInfo.maxPermitted"
                        value={values.quantityInfo.maxPermitted}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        placeholder="1"
                        className="w-full"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Quantité maximale que le client peut sélectionner pour
                        cette option.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end border-t border-gray-200 pt-6">
              <Button
                className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-lg font-bold shadow-md transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                disabled={isSubmitting || isLoading}
                type="submit"
              >
                {isLoading ? "Enregistrement..." : "Enregistrer l'option"}
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ModifierOptionForm;
